import {
  Tooltip,
  Typography,
  alpha,
  ClickAwayListener,
  useMediaQuery,
} from "@mui/material";
import React, { ReactNode, useState, useRef } from "react";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { IMinimizedTooltipProps } from "./MinimizedTooltip";

interface IClickableMinimizedTooltipProps extends IMinimizedTooltipProps {
  touchDelay?: number;
}

const useStyles = makeStyles({
  exerciseLibraryPopper: { zIndex: "99999999 !important" },
  exerciseActionButton: { width: "100%" },
});

export const TooltipLine = ({ children }: { children: ReactNode }) => (
  <Typography variant="hint">{children}</Typography>
);

/**
 * A clickable and interactive version of `MinimizedTooltip`.
 *
 * This component allows the tooltip to open and remain open when the user
 * clicks or taps on it, and close when the user clicks away or toggles it again.
 *
 * It supports:
 * - **Touch devices** — opens on touch.
 * - **Non-touch devices** — retains hover functionality.
 *
 * Use this component instead of `MinimizedTooltip` in situations where
 * the user might need to interact with the tooltip content (e.g. click a link, copy text, etc.).
 *
 */

const ClickableMinimizedTooltip = ({
  children,
  placement,
  title,
  opacity = 0.5,
  delayNextEnter = true,
  disableFocusListener = true,
  disableTouchListener,
  isExerciseLibrary = false,
  isExerciseActionButton = false,
  childrenClassName,
  touchDelay = 200,
}: IClickableMinimizedTooltipProps) => {
  const s = useStyles();
  // check if user is using touch device
  // if so, we will use touch and click events to open the tooltip
  // otherwise, we will use mouse events
  const isTouchMode = useMediaQuery("(pointer: coarse)");
  const [open, setOpen] = useState(false);
  const touchTimer = useRef<number>();

  const openTooltip = () => setOpen(true);
  const closeTooltip = () => {
    clearTimeout(touchTimer.current);
    setOpen(false);
  };

  const handleTouchStart = () => {
    touchTimer.current = window.setTimeout(openTooltip, touchDelay);
  };

  const handleTouchEnd = () => clearTimeout(touchTimer.current);

  const handlers = isTouchMode
    ? {
        onTouchStart: handleTouchStart,
        onTouchEnd: handleTouchEnd,
        onClick: openTooltip,
      }
    : {
        onMouseEnter: openTooltip,
        onMouseLeave: closeTooltip,
        onClick: openTooltip,
      };

  return (
    <ClickAwayListener onClickAway={closeTooltip}>
      <span>
        <Tooltip
          arrow
          open={open}
          onClose={closeTooltip}
          placement={placement}
          title={title}
          enterDelay={isTouchMode ? 0 : 300}
          enterNextDelay={isTouchMode ? 0 : delayNextEnter ? 3000 : 300}
          disableInteractive
          disableHoverListener={isTouchMode}
          disableFocusListener={disableFocusListener || isTouchMode}
          disableTouchListener={disableTouchListener || isTouchMode}
          classes={{ popper: isExerciseLibrary && s.exerciseLibraryPopper }}
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.grey[800], opacity)
                    : alpha(theme.palette.text.primary, opacity),
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? theme.palette.common.white
                    : undefined,
                px: 1,
                py: 0.8,
                userSelect: "none",
              },
            },
            arrow: {
              sx: {
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.grey[800], opacity)
                    : alpha(theme.palette.text.primary, opacity),
              },
            },
          }}
        >
          <span
            {...handlers}
            className={clsx(
              isExerciseActionButton && s.exerciseActionButton,
              childrenClassName,
            )}
          >
            {children}
          </span>
        </Tooltip>
      </span>
    </ClickAwayListener>
  );
};

export default ClickableMinimizedTooltip;
